import React from 'react';
import styled, { css } from 'styled-components';
import { Coord } from '@99designs/browser';
import { spaces } from '@99designs/emu/settings';
import { Tooltip } from '@99designs/spa-common';
import { AnnotationMarker } from '@99designs/spa-common';

const markerSize = 20; // px
const markerInnerSize = 8; // px

const textareaWidth = 300; // px
const minMessageHeight = 50; // px

const AnnotationMarkerWrapper = styled.div<{ position: Coord }>`
    position: absolute;
    height: ${markerSize}px;

    ${({ position: { x, y } }) => css`
        left: ${x}px;
        top: ${y}px;
    `}
`;

const AnnotationContent = styled.div`
    transition: height 0.1s;
    min-height: ${minMessageHeight}px;

    /* Use content-box so we can set the height as box grows or shrinks. */
    box-sizing: content-box;

    /* Set heights/widths with 'content-box' in mind. */
    width: ${textareaWidth - spaces.px['1bu'] * 2}px;
`;

export interface AnnotationProps {
    focused: boolean;
    children: React.ReactNode;
    position: Coord;
    onClick?: () => void;
}
function Annotation({ position, children, focused, onClick }: AnnotationProps) {
    return (
        <AnnotationMarkerWrapper
            position={position}
            onClick={(e) => {
                // Stop event propagation here so that clicking on the form
                // does not trigger the onClick event of the underlying FeedbackHandler ( which creates another annotation )
                e.stopPropagation();
            }}
        >
            <Tooltip
                open={focused}
                arrow
                interactive
                top={`${markerSize / 2}px`}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                title={<AnnotationContent>{children}</AnnotationContent>}
            >
                <AnnotationMarker
                    data-testid={focused ? 'annotation-focused' : 'annotation'}
                    focused={focused}
                    sizePx={markerSize}
                    innerSizePx={markerInnerSize}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onClick) {
                            onClick();
                        }
                    }}
                />
            </Tooltip>
        </AnnotationMarkerWrapper>
    );
}

export default Annotation;
